import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type BrandingAssetsList,
  type BrandingPatch,
  type BrandingSettings,
  deleteBrandingAsset,
  getBrandingSettings,
  listBrandingAssets,
  updateBrandingSettings,
  uploadBrandingAsset,
} from "@/api/admin/branding"

import { GC_TIME, SLOW_STALE_TIME } from "./_shared"

export function useBrandingSettingsQuery(enabled = true) {
  return useQuery<BrandingSettings>({
    queryKey: ["admin", "branding"],
    queryFn: getBrandingSettings,
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useBrandingAssetsQuery(enabled = true) {
  return useQuery<BrandingAssetsList>({
    queryKey: ["admin", "branding-assets"],
    queryFn: listBrandingAssets,
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateBrandingSettingsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: BrandingPatch) => updateBrandingSettings(patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "branding"] })
    },
  })
}

export function useUploadBrandingAssetMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ assetKey, file }: { assetKey: string; file: File }) =>
      uploadBrandingAsset(assetKey, file),
    onSuccess: () => {
      // Asset writes must not invalidate ["admin", "branding"] (form reseeds).
      void queryClient.invalidateQueries({ queryKey: ["admin", "branding-assets"] })
    },
  })
}

export function useDeleteBrandingAssetMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (assetKey: string) => deleteBrandingAsset(assetKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "branding-assets"] })
    },
  })
}
